import { get, writable } from 'svelte/store';

import { set_under_system_bars_android } from 'src/services/native_container_requests_android';
import { app, os } from 'src/stores/session/native-bridge';

const STATUS_BAR_KEY = 'shelly_dev_under_status_bar';
const NAVIGATION_BAR_KEY = 'shelly_dev_under_navigation_bar';

const readInitialValue = (key: string, fallback = true): boolean => {
	try {
		const stored = localStorage.getItem(key);
		if (stored === null) return fallback;
		return stored === 'true';
	} catch {
		return fallback;
	}
};

const persist = (key: string, value: boolean) => {
	try {
		localStorage.setItem(key, String(value));
	} catch {
		// localStorage may be unavailable
	}
};

/**
 * When true, asks the Android native shell to draw the WebView under the status bar
 * (`shelly://under_system_bars/?underStatusBar=true`). Defaults to true (edge-to-edge).
 */
export const underStatusBarEnabled = writable<boolean>(readInitialValue(STATUS_BAR_KEY));

/**
 * When true, asks the Android native shell to draw the WebView under the navigation bar
 * (`shelly://under_system_bars/?underNavigationBar=true`). Defaults to true (edge-to-edge).
 */
export const underNavigationBarEnabled = writable<boolean>(readInitialValue(NAVIGATION_BAR_KEY));

underStatusBarEnabled.subscribe((value) => persist(STATUS_BAR_KEY, value));
underNavigationBarEnabled.subscribe((value) => persist(NAVIGATION_BAR_KEY, value));

export const isUnderStatusBarEnabled = (): boolean => get(underStatusBarEnabled);
export const isUnderNavigationBarEnabled = (): boolean => get(underNavigationBarEnabled);

const isAndroidNative = (): boolean => get(app) && get(os) !== 'IOS';

/** Push the current preferences to the Android native shell (no-op off Android). */
export const applyUnderSystemBarsAndroid = (
	underStatusBar: boolean = get(underStatusBarEnabled),
	underNavigationBar: boolean = get(underNavigationBarEnabled),
) => {
	if (!isAndroidNative()) return;
	set_under_system_bars_android(underStatusBar, underNavigationBar);
};

export const setUnderStatusBarEnabled = (enabled: boolean) => {
	underStatusBarEnabled.set(enabled);
	applyUnderSystemBarsAndroid(enabled, get(underNavigationBarEnabled));
};

export const setUnderNavigationBarEnabled = (enabled: boolean) => {
	underNavigationBarEnabled.set(enabled);
	applyUnderSystemBarsAndroid(get(underStatusBarEnabled), enabled);
};

/** Set both bars at once (e.g. both off / both on). */
export const setUnderSystemBarsEnabled = (underStatusBar: boolean, underNavigationBar: boolean) => {
	underStatusBarEnabled.set(underStatusBar);
	underNavigationBarEnabled.set(underNavigationBar);
	applyUnderSystemBarsAndroid(underStatusBar, underNavigationBar);
};
